/**
 * The ONE scaffolding guard, shared by the PANEL and the markdown EXPORT.
 *
 * It lives here rather than in either consumer because it was duplicated and immediately drifted:
 * hardening the panel's set with the synthesis-SECTION keys left `reportUtils.ts`'s own copy behind,
 * so a generated workflow's `rootCauseHypothesis` / `mechanismSynthesis` was hidden on screen and
 * still written into the downloaded report (graphite #26045). The export is the surface with no app
 * to fall back to — the same lesson as #25976 — so the two must not be able to disagree. Copying the
 * hardened list into the second file would have fixed today and drifted again on the next key.
 *
 * Pure module: no React, so the report path does not pull in the panel.
 */

/**
 * Internal / plumbing keys that must never surface as a user-facing finding even if they ride along
 * on a general workflow's declared output. This is the guard that kept the reverted #26005's dump
 * (`status:"hypothesis"`, `evidence_refs:[]`, rationale JSON) off the panel — applied at every level.
 *
 * Two KINDS live here, and the second was missing. FIELD-level markers (`status`, `evidence_refs`, …)
 * ride *inside* a section. SYNTHESIS-SCAFFOLDING SECTION keys are whole sections of intermediate
 * reasoning — `rootCauseHypothesis`, `mitigationAndPrevention`, `reversalMechanismSynthesis` — and
 * they are reachable here: a GENERATED general workflow declares no `artifact.resultKeys`, so
 * `withGeneralResult` falls back to the final synthesis record, whose own fields are exactly those
 * names. Rendering them reproduces the flow-reversal dump #26032 reverted (self-review #26045).
 */
export const GENERAL_RESULT_EXCLUDE = new Set<string>([
  // field-level markers
  'status',
  'evidenceRefs',
  'evidence_refs',
  'rationale',
  'reasoning',
  'degradedFromText',
  'workflowToolQuotaStop',
  'softTimeGuardReached',
  // troubleshooting_base's LOOP-EXIT control boolean (`outputs.hypothesisAssessment.
  // assessmentComplete` drives the assess-loop's exit condition). Same class as the two flags above,
  // and it was rendering as a finding section — "Assessment complete: true" appeared in a live report
  // (2026-07-30) as though it were a result. It also mattered to the verdict: `hasGeneralContent`
  // counts any boolean as content, so a run whose only general section was this flag would have read
  // as a delivered ANSWER (resultHasAnswer).
  'assessmentComplete',
  'assessment_complete',
  'text',
  // synthesis-scaffolding SECTION keys — the names actually observed in the reverted run
  'rootCauseHypothesis',
  'root_cause_hypothesis',
  'mitigationAndPrevention',
  'mitigation_and_prevention',
  'reversalMechanismSynthesis',
  'reversal_mechanism_synthesis',
  'primaryDrivers',
  'primary_drivers',
  'primaryHypothesis',
  'primary_hypothesis',
  'mechanisms',
  // NB: `failureModes` is deliberately NOT here — it is a DECLARED section of the shipped
  // fmea-analysis template (`resultKeys: [… failureModes, riskRanking, recommendations]`), so
  // excluding it would hide a real result section. The backend consumes the singular derive inputs
  // (`mechanisms`, `primaryHypothesis`) into `outcome.probableHypotheses`; those never double as a
  // declared section, which is why only they are listed.
  'criticalDataGaps',
  'critical_data_gaps',
  'dataGaps',
  'data_gaps',
])

/**
 * …and a SHAPE rule beside the name list, because a name list drifts: the model invents new
 * scaffolding names freely, so pinning only the observed ones guarantees the next generated
 * workflow leaks a differently-spelled section. A key whose name ENDS in a reasoning noun is
 * intermediate analysis by construction (`…Hypothesis`, `…Synthesis`, `…Rationale`, `…Reasoning`,
 * `…Scaffold`, `…Draft`), which no legitimate declared section of the shipped templates matches
 * (`nodeScope`, `designBasis`, `deviationWorksheet`, `scenarioAssessment`, `failureModes`,
 * `riskRanking`, `alarmFindings`, `balanceComputation`, `recommendations`, …) — pinned by a test that
 * runs every shipped template's declared `resultKeys` through this predicate.
 */
// The leading `.+` is load-bearing: the rule targets COMPOUND names the model invents for
// intermediate reasoning (`rootCauseHypothesis`, `mechanismSynthesis`, `selectionRationale`), and
// without it the BARE plural was swallowed too — a declared `hypotheses` section was deleted from
// both the panel and the export, which is a real result section, not scaffolding (found while fixing
// #26064's export dedup). A bare `hypotheses` / `synthesis` / `rationale` key is a section name; a
// compound one is a reasoning step.
//
// The rule stays PLURAL-TOLERANT (`hypothes[ie]s`) and the collision is handled by exempting the
// artifact field NAMES instead — see {@link ARTIFACT_FIELD_NAMES}. Narrowing the rule to the singular
// fixed `probableHypotheses` but traded away the protection the rule exists for: `rootCauseHypotheses`
// is exactly the kind of name the model invents, and the singular-only form let it through
// (graphite #26064). Artifact fields are a CLOSED set we control; invented scaffolding names are not,
// so the exemption belongs on the side we can enumerate.
export const SCAFFOLDING_KEY_SUFFIX_RE =
  /.+(?:hypothes[ie]s|synthesis|rationale|reasoning|scaffold|scaffolding|draft|wip)$/i

/**
 * Real artifact field names that the SHAPE rule would otherwise match. Exempt because they are DATA a
 * template declares, not reasoning the model invented — `probableHypotheses` is an outcome list, and
 * hiding it deleted a genuine `generalResult.outcome` section from both surfaces.
 *
 * Membership criterion, so this does not become a junk drawer: a name belongs here only if it is a
 * field of the troubleshooting artifact AND `SCAFFOLDING_KEY_SUFFIX_RE` matches it. `confirmedRootCauses`
 * is listed defensively — it is an artifact field that the rule does not match today, so it is exempt
 * regardless of how the suffix list grows.
 */
const ARTIFACT_FIELD_NAMES = new Set<string>([
  'hypotheses',
  'probableHypotheses',
  'confirmedRootCauses',
])

/** True when a `generalResult` key is internal scaffolding rather than a user-facing section. */
export function isScaffoldingKey(key: string): boolean {
  // Exemption FIRST: a declared artifact field is never scaffolding, whatever its shape.
  if (ARTIFACT_FIELD_NAMES.has(key)) return false
  return GENERAL_RESULT_EXCLUDE.has(key) || SCAFFOLDING_KEY_SUFFIX_RE.test(key)
}

/* ------------------------------------------------------------------------------------------------
 * The rest of the general-result curation was DUPLICATED between the panel (`WorkflowResultPanel.tsx`)
 * and the markdown export (`reportUtils.ts`): a lead-key list, a recursive content gate, a one-line
 * flattener, and a key humanizer — four functions, two copies each. The exclude set moving here (above)
 * closed only part of the drift the module's header promises to close: the two copies still diverged
 * (the export printed a `fail_open` value raw where the panel printed "Fail open"; the panel titled a
 * `deviationWorksheet` section in raw camelCase where the export wrote "Deviation worksheet"), and the
 * export had no fixed-section dedup so it double-rendered `actions` (## Corrective action AND
 * ## Result → Actions — graphite #26045, reported unfixed). These live here now so the two surfaces
 * read from ONE implementation and cannot disagree. Pure (no React), so the report path stays panel-free.
 * ---------------------------------------------------------------------------------------------- */

function grStr(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}
function grRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}
function grArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

/** Fields whose value can stand in as an entry's headline (first non-empty wins). */
export const GENERAL_LEAD_KEYS = ['name', 'title', 'statement', 'label', 'summary', 'description', 'id']

/**
 * Artifact keys a DEDICATED section already renders (on BOTH surfaces: the panel's fixed Sections and
 * the export's `## Corrective action` / diagnosis / problem blocks). A declared `generalResult` often
 * carries the same key — the assembler fills the fixed field from the SAME parts key — so rendering it
 * again in the generic loop showed it twice. `generalResultKeys` drops a key ONLY when its fixed
 * section actually has content ("render once", never "drop"): an empty fixed field still lets the
 * generic section show it.
 */
/**
 * Fixed artifact fields the PANEL renders with a dedicated section — so a `generalResult` key of the
 * same name is already on screen and must not be repeated.
 */
export const PANEL_FIXED_SECTION_KEYS = [
  'problemStatement',
  'primaryCandidate',
  'actions',
  'causeMap',
  'hypotheses',
  'connectedSystemsChecked',
  'safety',
  'learningUpdates',
  'analysisNarratives',
  'outcome',
  'evidencePack',
  // `symptomCauseMap`, `operatorResults` and `clarifications` are deliberately ABSENT: no panel section
  // renders them (0 references), so listing them suppressed a generalResult copy that nothing else
  // showed — the panel-side twin of the export defect below (#26063).
]

// There is deliberately NO export-side key list any more. `reportMarkdown` records the fixed key each
// heading consumed as it emits it and passes that set to `generalResultKeys`, so ownership is DERIVED
// from what the surface actually rendered. A mirrored list was wrong in both directions — the export
// reads different fields than the panel — and a second hand-maintained mirror would drift the same way
// the first did (graphite #26064).

/** @deprecated Use {@link PANEL_FIXED_SECTION_KEYS}. Kept as the panel's alias for existing importers. */
export const FIXED_SECTION_KEYS = PANEL_FIXED_SECTION_KEYS

/** `hazopNode` / `hazop_node` → "Hazop node". Handles camelCase AND snake/kebab (the panel's old
 *  enum-only humanizer left camelCase keys raw — the export's fuller form is the shared one). */
export function humanizeGeneralKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Humanize a scalar VALUE conservatively: a lowercase snake_case enum (`fail_open`) → "Fail open";
 *  tags / IDs / mixed-case / prose are left verbatim (never mangle `FV1110` or `AIT-1006`). */
/**
 * Identifier/reference field names whose values are never enum-humanized, so an underscore-namespaced
 * id (e.g. `ev_define_problem`, `hyp_interlock_trigger`) is preserved verbatim rather than mangled
 * into prose. Covers `id`, camelCase `*Id`/`*Ref`, and snake `*_id`/`*_ref`.
 *
 * Moved here from the panel as part of the same consolidation: it is another string helper both
 * surfaces need, and importing it FROM the panel would invert the dependency (this module is
 * deliberately React-free).
 */
export function isIdLikeKey(key: string): boolean {
  return /(^id$|Id$|Ref$|_id$|_ref$)/.test(key)
}

/**
 * A scalar rendered for display: a single lowercase snake token is enum-humanized (`fail_open` →
 * "Fail open"), anything else passes through.
 *
 * `key` is what makes that safe. Without it the export mangled the snake-namespaced IDs this codebase
 * actually emits — `evidenceRef: 'ev_define_problem'` printed as "Ev define problem", where the export
 * previously used `String(v)` and printed it verbatim (self-review #26064). The panel already had this
 * guard (`isIdLikeKey`); the consolidation dropped it by taking no key.
 */
export function formatGeneralScalar(value: unknown, key?: string): string {
  if (typeof value !== 'string') return String(value)
  if (key !== undefined && isIdLikeKey(key)) return value
  if (!/^[a-z]+(_[a-z]+)*$/.test(value)) return value
  const spaced = value.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Recursive content gate: is there any user-facing (non-scaffolding) scalar anywhere under `value`? */
export function hasGeneralContent(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'number' || typeof value === 'boolean') return true
  if (Array.isArray(value)) return value.some(hasGeneralContent)
  const rec = grRecord(value)
  if (rec) return Object.entries(rec).some(([k, v]) => !isScaffoldingKey(k) && hasGeneralContent(v))
  return false
}

/** Readable scalar fields of a record joined to one line (`Guideword: No flow; Severity: High`). */
function recordScalarLine(rec: Record<string, unknown>): string {
  return Object.entries(rec)
    .filter(
      ([k, v]) =>
        !isScaffoldingKey(k) && v !== null && v !== undefined && typeof v !== 'object' && String(v) !== '',
    )
    .map(([k, v]) => `${humanizeGeneralKey(k)}: ${formatGeneralScalar(v, k)}`)
    .join('; ')
}

/**
 * Flatten ONE nested field to a readable line — never `JSON.stringify`. A list collapses to each
 * entry's lead field, OR — the fix for the gate/renderer disagreement (graphite #26045) — to its
 * readable scalar fields when it has NO lead key (a `{guideword, causes}` deviation has neither
 * name/title/…; the gate counted it as content via `guideword`, but the old flattener only looked for
 * a lead key and returned '' → an empty section). A record collapses to its scalar fields. A deep
 * object with no readable scalar is OMITTED (returns '') — dumping it is the rejected behaviour.
 */
export function generalInline(value: unknown, key?: string): string {
  if (value === null || value === undefined) return ''
  // `key` threaded so an id-like field stays verbatim at EVERY level — the leak was here, not only at
  // the top: `{ scope: { evidenceRef: 'ev_define_problem' } }` reaches this scalar branch through the
  // record path, where the caller knows the key and this function was throwing it away.
  if (typeof value !== 'object') return formatGeneralScalar(value, key)
  const arr = grArray(value)
  if (arr.length) {
    return arr
      .map((x) => {
        // `key` here too: a LIST of id-like values ("evidenceRef": ['ev_a','ev_b']) was humanized
        // entry-by-entry while the scalar form was verbatim (graphite #26064).
        if (typeof x !== 'object' || x === null) return formatGeneralScalar(x, key)
        const r = grRecord(x)
        if (!r) return ''
        const lk = GENERAL_LEAD_KEYS.find((k) => grStr(r[k]))
        return lk ? grStr(r[lk]) : recordScalarLine(r)
      })
      .filter(Boolean)
      .join('; ')
  }
  const rec = grRecord(value)
  return rec ? recordScalarLine(rec) : ''
}

/**
 * Top-level `generalResult` keys carrying user-facing content: excludes internal/scaffolding markers,
 * empties, and anything a dedicated fixed section is already rendering. The ONE key-selection both
 * surfaces use — the export used a looser filter (no fixed-section dedup) and double-rendered.
 */
/**
 * PORTED FROM THE PANEL (#26063): what each fixed section actually RENDERS, per item.
 *
 * Ownership must use the SECTION's own predicate, not the generic content gate — a fixed value the
 * section renders nothing for (`actions: [{owner:'ops'}]`, `analysisNarratives: [{step:'x'}]`) would
 * otherwise claim the key and suppress a real `generalResult` copy: "render once" becoming "drop".
 * These live here now so the panel's sections and the ownership test share one definition.
 */
export const SECTION_ITEM_HAS_CONTENT: Record<string, (item: Record<string, unknown>) => boolean> = {
  actions: (a) => Boolean(grStr(a.action)),
  causeMap: (n) => Boolean(grStr(n.statement)),
  // A hypothesis renders its statement AND its proposed checks, so either one is content: filtering
  // on `statement` alone made `{ proposedControlledChecks: [{ check: '…' }] }` disappear — real
  // content, not a blank row.
  hypotheses: (h) => Boolean(grStr(h.statement)) || grArray(h.proposedControlledChecks).length > 0,
  connectedSystemsChecked: (c) => Boolean(grStr(c.subject) || grStr(c.finding)),
  learningUpdates: (u) => Boolean(grStr(u.recommendation) || grStr(u.target)),
  blockedClaims: (b) => Boolean(grStr(b.claim)),
  // AnalysisNarrativesSection filters on `.text`, so a step marker with no prose renders nothing —
  // and a parse-failed synthesis step emits exactly that. Left to the generic fallback, it suppressed
  // a `generalResult.analysisNarratives` carrying REAL prose (self-review #26063 follow-up).
  analysisNarratives: (n) => Boolean(grStr(n.text)),
  evidencePack: () => true, // EvidenceSection gates on presence only
}

/**
 * The same thing for the fixed fields that are RECORDS rather than arrays — each mirrors its section's
 * own empty-check, so ownership means "the section is rendering it" for every key, not just the
 * array-shaped ones.
 */
export const SECTION_VALUE_HAS_CONTENT: Record<string, (value: unknown) => boolean> = {
  // ProblemSection: `if (!observed && !facts.length && !isNot.length) return null`
  problemStatement: (v) => {
    const p = grRecord(v)
    if (!p) return false
    return Boolean(
      grStr(p.observedDeviation) ||
        grStr(p.expectedState) ||
        grStr(p.actualState) ||
        grStr(p.extent) ||
        grArray(p.isNot).map(grStr).filter(Boolean).length,
    )
  },
  // PrimaryCandidateSection: `if (!c || !grStr(c.statement)) return null`
  primaryCandidate: (v) => Boolean(grStr(grRecord(v)?.statement)),
  // SafetySection renders a status and/or safety instructions.
  safety: (v) => {
    const sf = grRecord(v)
    if (!sf) return false
    return Boolean(
      grStr(sf.status) ||
        grArray(sf.safetyInstructions).map((x) => grStr(grRecord(x)?.instruction)).filter(Boolean).length,
    )
  },
  // `outcome` is rendered by the verdict line + the Still-uncertain panel, so any of its lists counts.
  outcome: (v) => {
    const o = grRecord(v)
    if (!o) return false
    return (
      grArray(o.confirmedRootCauses).length > 0 ||
      grArray(o.probableHypotheses).length > 0 ||
      sectionItems('blockedClaims', o.blockedClaims).length > 0 ||
      grArray(o.remainingUncertainty).map(grStr).filter(Boolean).length > 0 ||
      grArray(o.assumptions).map(grStr).filter(Boolean).length > 0
    )
  },
}

/** The renderable items of a fixed ARRAY section. */
export function sectionItems(key: string, value: unknown): Record<string, unknown>[] {
  const items = grArray(value).map(grRecord).filter(Boolean) as Record<string, unknown>[]
  const predicate = SECTION_ITEM_HAS_CONTENT[key]
  return predicate ? items.filter(predicate) : items
}

/**
 * Whether the fixed section for `key` is rendering anything. NO generic fallback: an unlisted key owns
 * nothing, so the failure direction is a duplicate (visible) rather than a drop (silent), and the
 * suite asserts every list entry has a predicate.
 */
export function fixedSectionHasContent(key: string, value: unknown): boolean {
  if (key in SECTION_ITEM_HAS_CONTENT) return sectionItems(key, value).length > 0
  const valuePredicate = SECTION_VALUE_HAS_CONTENT[key]
  if (valuePredicate) return valuePredicate(value)
  return false
}

/**
 * For a caller that KNOWS what it rendered (the markdown export records each heading's source key):
 * membership is the ownership, with no predicate consulted. Running the panel's predicates over a
 * derived set re-broke the double-render — the export renders a `problemStatement` from
 * `.text || .statement`, which the panel's predicate calls empty, so the key was "not owned" and got
 * emitted twice (graphite #26064).
 */
export function generalResultKeysExcluding(
  output: Record<string, unknown> | undefined,
  renderedKeys: readonly string[],
): string[] {
  const result = grRecord(output?.generalResult)
  if (!result) return []
  const rendered = new Set(renderedKeys)
  return Object.keys(result).filter(
    (key) => !isScaffoldingKey(key) && !rendered.has(key) && hasGeneralContent(result[key]),
  )
}

export function generalResultKeys(
  output: Record<string, unknown> | undefined,
  fixedSectionKeys: readonly string[] = PANEL_FIXED_SECTION_KEYS,
): string[] {
  const result = grRecord(output?.generalResult)
  if (!result) return []
  const ownedByFixedSection = new Set<string>(
    fixedSectionKeys.filter((key) => fixedSectionHasContent(key, output?.[key])),
  )
  return Object.keys(result).filter(
    (key) => !isScaffoldingKey(key) && !ownedByFixedSection.has(key) && hasGeneralContent(result[key]),
  )
}


/* ------------------------------------------------------------------------------------------------
 * THE ANSWER DECIDER — one predicate for every human-facing verdict.
 *
 * `run.status` is the ENGINE's honesty grade. It can read amber for reasons that are invisible in
 * the rendered result — a general-family artifact carries no `confirmedRootCauses` so the audit's
 * troubleshooting fallback graded it "unknowns"; the interim seeded-answer scaffold keeps its own
 * grade deliberately — which produced the reported defect: a HAZOP node definition whose sections
 * were fully populated, whose three steps all succeeded, reported "Completed · open questions"
 * (2026-07-30). Grading a REPORT by that status asks the reader to reconcile a warning with an
 * answer sitting right underneath it.
 *
 * So the human-facing verdict is decided HERE, from the same content gates that decide what renders:
 * if the reader can see findings, the run delivered; if the reader can see blocked claims, it has
 * open questions. Pill, banner and Markdown export all call these, so no surface can contradict
 * another — and the engine's status stays untouched for evals, emails and automation.
 * ---------------------------------------------------------------------------------------------- */

/**
 * Sections that are present on a run which produced NOTHING, so none of them can stand in for an
 * answer: `problemStatement` echoes the user's own question back, `safety` is an "Unknown"
 * placeholder on any workflow with no safety verdict, `evidencePack` is the SUPPORT for an answer
 * rather than the answer, and `outcome` mixes findings with caveats (graded by
 * {@link outcomeHasFindings} instead).
 */
const NON_ANSWER_SECTION_KEYS = new Set(['problemStatement', 'safety', 'evidencePack', 'outcome'])

/** `outcome` counts as an answer only for its FINDINGS lists, never its caveats. */
export function outcomeHasFindings(output: Record<string, unknown> | undefined): boolean {
  const outcome = grRecord(output?.outcome)
  if (!outcome) return false
  return (
    grArray(outcome.confirmedRootCauses).length > 0 || grArray(outcome.probableHypotheses).length > 0
  )
}

/**
 * A v2 run's answer IS its top-level `text`, and that is the whole artifact.
 *
 * v1 answers are structured sections, so every gate below enumerates those — and `text` sits in
 * the general-result exclusion list precisely because on a v1 run it is scaffolding. On a v2 run
 * it is the answer: the engine ships db_ask's own words verbatim rather than restating them into
 * sections. The consequence was the exact defect this decider was written to kill, one engine
 * later — a completed v2 run with a correct answer on screen graded "No result" under an amber
 * pill, because none of the v1 section keys were present.
 *
 * Gated on the engine stamp, so v1 grading is byte-identical. Read from `finalOutput` rather than
 * `run.state`, because the chat-side result block carries no `state` and this predicate is handed
 * only the output.
 */
function v2TextAnswer(output: Record<string, unknown>): boolean {
  if (output.engine !== 'v2') return false
  // An honest "the searches returned nothing" is completed v2 text, but it is not a finding.
  // Without this, the panel/report graded that path "Clean run" (Greptile on #28782).
  if (output.absence === true) return false
  const text = output.text
  return typeof text === 'string' && text.trim() !== ''
}

/** Does the rendered result carry findings a reader can point at? */
export function resultHasAnswer(output: Record<string, unknown> | undefined): boolean {
  if (!output) return false
  if (v2TextAnswer(output)) return true
  if (generalResultKeys(output).length > 0) return true
  if (outcomeHasFindings(output)) return true
  return PANEL_FIXED_SECTION_KEYS.some(
    (key) => !NON_ANSWER_SECTION_KEYS.has(key) && fixedSectionHasContent(key, output[key]),
  )
}

/**
 * How many open questions the result SHOWS — blocked claims, the only "could not determine this"
 * list rendered as such. This is what makes an "open questions" verdict honest: they are on screen.
 * Read from `outcome.blockedClaims` and the top-level lift the normalizer sometimes produces (the
 * panel reads both).
 */
export function openQuestionCount(output: Record<string, unknown> | undefined): number {
  if (!output) return 0
  const nested = grRecord(output.outcome)?.blockedClaims
  const claims = grArray(nested).length > 0 ? nested : output.blockedClaims
  return sectionItems('blockedClaims', claims).length
}
